import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, MoreVertical, AlertTriangle, Folder, FolderPlus, Search } from 'lucide-react';
import { useStore } from '@tanstack/react-store';
import { selectedLanguageStore, setSelectedLanguage } from '../../../stores/selectedLanguage';
import { useListLanguages } from '../../../hooks/useLanguages';
import { useGetInterestCategories, useCreateInterestCategory, useDeleteInterestCategory, useUpdateInterestCategory, useUpdateInterestCategoryStatus } from '../../../hooks/useInterest';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

export default function InterestCategories() {
    const { selectedLanguage } = useStore(selectedLanguageStore)
    const [languages, setLanguages] = useState([])
    const [categories, setCategories] = useState([])
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const createInterestCategoryMutation = useCreateInterestCategory()
    const deleteInterestCategoryMutation = useDeleteInterestCategory()
    const updateInterestCategoryMutation = useUpdateInterestCategory()
    const updateStatusMutation = useUpdateInterestCategoryStatus()
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(null);
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingCategory, setEditingCategory] = useState(null);
    const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
    const [deletingCategory, setDeletingCategory] = useState(null);
    const [showDeleteCategoryConfirmation, setShowDeleteCategoryConfirmation] = useState(false);
    const { data: languagesListResponse, isSuccess: isSuccessLanguagesList } = useListLanguages('')
    const { data: interestCategoryResponse, isSuccess: isSuccessInterestCategory } = useGetInterestCategories(`?language=${selectedLanguage}&search_key=${searchTerm}`)

    // Pagination functions
    let totalPages = Math.ceil(categories?.filter(category => category?.title?.toLowerCase().includes(searchTerm?.toLowerCase()))?.length / rowsPerPage)
    totalPages = totalPages == 0 ? 1 : totalPages

    const toggleDropdown = (id) => {
        setShowDropdown(showDropdown === id ? null : id);
    };

    const handleEditCategory = () => {
        if (editingCategory) {
            if (editingCategory?.title?.trim() == "") {
                toast.error("Category name is not allowed to empty")
                return false
            }
            updateInterestCategoryMutation.mutate({
                title: editingCategory?.title,
                language: selectedLanguage,
                _id: editingCategory?._id
            }, {
                onSuccess: () => {
                    toast.success("Category Updated Successfully")
                    queryClient.invalidateQueries({ queryKey: ['interest_categories'] });
                    setEditingCategory(null);
                    setShowEditCategoryModal(false);
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        }
    };

    const handleDeleteCategory = () => {
        if (deletingCategory) {
            deleteInterestCategoryMutation.mutate(
                deletingCategory?._id, {
                onSuccess: () => {
                    toast.success("Category has been deleted successfully")
                    queryClient.invalidateQueries({ queryKey: ['interest_categories'] });
                    setShowDeleteCategoryConfirmation(false);
                    setDeletingCategory(null);
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        }
    };

    const openEditCategoryModal = (category) => {
        setEditingCategory({ ...category });
        setShowEditCategoryModal(true);
        setShowDropdown(null);
    };

    const openDeleteCategoryConfirmation = (category) => {
        setDeletingCategory(category);
        setShowDeleteCategoryConfirmation(true);
        setShowDropdown(null);
    };

    const handleAddCategory = () => {
        if (newCategoryName == "") {
            toast.error("Category name is not allowed to empty")
            return false
        }
        createInterestCategoryMutation.mutate({
            title: newCategoryName,
            language: selectedLanguage
        }, {
            onSuccess: () => {
                toast.success("New Category added Successfully")
                queryClient.invalidateQueries({ queryKey: ['interest_categories'] });
                setNewCategoryName('');
                setShowAddCategoryModal(false)
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    }

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const toggleStatus = (id, currentStatus) => {
        updateStatusMutation.mutate({
            status: currentStatus == 1 ? 3 : 1,
            _id: id
        }, {
            onSuccess: () => {
                toast.success("Category status updated.")
                queryClient.invalidateQueries({ queryKey: ['interest_categories'] });
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    };

    useEffect(() => {
        if (isSuccessInterestCategory) {
            setCategories(interestCategoryResponse?.data?.data?.result)
        }
    }, [interestCategoryResponse, isSuccessInterestCategory])

    useEffect(() => {
        if (isSuccessLanguagesList) {
            setLanguages(languagesListResponse?.data?.data?.result)
        }
    }, [languagesListResponse, isSuccessLanguagesList])

    return (
        <div className="flex h-screen bg-white text-gray-800">
            <div className="flex-1 flex flex-col">
                {/* Breadcrumb */}
                <div className="border-b border-gray-200 p-4 flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Dashboard</span>
                    <ChevronRight size={14} className="text-gray-400" />
                    <span>Interest's</span>
                </div>
                {/* Content */}
                <div className="p-6 flex justify-between">
                    <div className="mb-6">
                        <h1 className="text-2xl font-semibold mb-2">Interest's</h1>
                        <p className="text-gray-500">Manage Interest's for users to see on your app and the website.</p>
                    </div>
                    <div className="mb-6 mt-4">
                        <button
                            onClick={() => setShowAddCategoryModal(true)}
                            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 flex items-center gap-2">
                            <FolderPlus size={16} />
                            Add New Category
                        </button>
                    </div>
                </div>
                <div className="mb-6 flex space-x-2">
                    {languages.map(language => (
                        <button
                            key={language?.short_code}
                            className={`px-4 py-2 rounded ${selectedLanguage === language?.short_code ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-600'}`}
                            onClick={() => setSelectedLanguage(language?.short_code)}>
                            {language?.name}
                        </button>
                    ))}
                </div>
                {/* Search */}
                <div className="mb-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search by category name"
                            className="w-full p-2 pl-10 border border-gray-300 rounded"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <Search size={18} className="absolute left-3 top-3 text-gray-400" />
                    </div>
                </div>
                {/* Categories Table */}
                <div className="border border-gray-200 rounded mb-4">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="px-4 py-3 text-left w-10">S.No</th>
                                <th className="px-4 py-3 text-left">Category Name</th>
                                <th className="px-4 py-3 text-left">Sub Categories</th>
                                <th className="px-4 py-3 text-center">Status</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map((category, i) => {
                                const subCategories = category?.interest_subcategories?.map((sc) => sc?.title).join(", ");
                                return (
                                    <tr key={category?._id} className="border-b border-gray-200">
                                        <td className="px-4 py-3">{i + 1}</td>
                                        <td
                                            onClick={() => navigate(`/interest/${category?._id}`)}
                                            className="px-4 py-3 flex items-center cursor-pointer">
                                            <Folder size={16} className="mr-2 text-gray-500" />
                                            {category?.title}
                                        </td>
                                        <td 
                                            onClick={() => navigate(`/interest/${category?._id}`)}
                                            className="px-4 py-3 cursor-pointer">
                                            {subCategories}
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <button
                                                onClick={() => toggleStatus(category?._id, category?.status)}
                                                className="focus:outline-none">
                                                <div className={`w-12 h-6 rounded-full flex items-center ${category?.status == 1 ? 'bg-gray-900' : 'bg-gray-300'} p-1 transition-colors duration-200`}>
                                                    <div className={`w-4 h-4 rounded-full bg-white transform transition-transform duration-200 ${category.status == 1 ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                </div>
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="relative inline-block">
                                                <button
                                                    onClick={() => toggleDropdown(category?._id)}
                                                    className="p-1 hover:bg-gray-100 rounded-full">
                                                    <MoreVertical size={16} />
                                                </button>
                                                {showDropdown === category?._id && (
                                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded shadow-lg border border-gray-200 z-10">
                                                        <div className="py-1">
                                                            <button
                                                                onClick={() => openEditCategoryModal(category)}
                                                                className="px-4 py-2 text-sm w-full text-left hover:bg-gray-100">
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => openDeleteCategoryConfirmation(category)}
                                                                className="px-4 py-2 text-sm w-full text-left hover:bg-gray-100 flex items-center gap-2">
                                                                Delete
                                                                <span className="text-xs text-gray-500 ml-auto">⌘⌫</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                <div className="flex justify-end">
                    <div className="flex items-center">
                        <div className="mr-4 flex justify-between">
                            <span className="mt-1 mr-2 text-sm text-gray-500">Rows per page</span>
                            <select
                                className="w-[55px] border border-gray-300 rounded p-1 text-sm"
                                value={rowsPerPage}
                                onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                        <div className="flex items-center text-sm">
                            <span className="mr-4">Page {currentPage} of {totalPages}</span>
                            <div className="flex">
                                <button
                                    className="p-1 border border-gray-300 rounded-l hover:bg-gray-100"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}>
                                    <ChevronsLeft size={16} />
                                </button>
                                <button
                                    className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}>
                                    <ChevronLeft size={16} />
                                </button>
                                <button
                                    className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}>
                                    <ChevronRight size={16} />
                                </button>
                                <button
                                    className="p-1 border border-gray-300 rounded-r hover:bg-gray-100"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}>
                                    <ChevronsRight size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Add Category Modal */}
            {showAddCategoryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold mb-4">Add Category</h2>
                            <p className="text-gray-500 text-sm mb-6">Create a new category for organizing Interest</p>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Category Name</label>
                                    <input
                                        type="text"
                                        value={newCategoryName}
                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                        className="block w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter category name..."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-4 bg-gray-50 border-t border-gray-200 rounded-b-md">
                            <button
                                onClick={() => setShowAddCategoryModal(false)}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded mr-2">
                                Cancel
                            </button>
                            <button
                                onClick={() => handleAddCategory()}
                                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700">
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Edit Category Modal */}
            {showEditCategoryModal && editingCategory && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold mb-4">Edit Category</h2>
                            <p className="text-gray-500 text-sm mb-6">Update the category name</p>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Category Name</label>
                                    <input
                                        type="text"
                                        value={editingCategory.title}
                                        onChange={(e) => setEditingCategory({ ...editingCategory, title: e.target.value })}
                                        className="block w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter category name..."
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end p-4 bg-gray-50 border-t border-gray-200 rounded-b-md">
                            <button
                                onClick={() => {
                                    setEditingCategory(null);
                                    setShowEditCategoryModal(false);
                                }}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded mr-2">
                                Cancel
                            </button>
                            <button
                                onClick={handleEditCategory}
                                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700">
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete Category Confirmation Modal */}
            {showDeleteCategoryConfirmation && deletingCategory && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="p-6">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-500">
                                    <AlertTriangle size={20} />
                                </div>
                                <h2 className="text-xl font-semibold">Delete Category</h2>
                            </div>

                            <p className="text-gray-600 mb-4">
                                Are you sure you want to delete this category? This will also delete all associated sub categories. This action cannot be undone.
                            </p>

                            <div className="p-4 border rounded bg-gray-50 mb-4">
                                <div className="flex items-center mb-2">
                                    <Folder size={16} className="mr-2 text-gray-500" />
                                    <p className="font-medium">{deletingCategory.title}</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end p-4 bg-gray-50 border-t border-gray-200 rounded-b-md">
                            <button
                                onClick={() => {
                                    setDeletingCategory(null);
                                    setShowDeleteCategoryConfirmation(false);
                                }}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded mr-2">
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteCategory}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}